/**
 * Team & Ownership Mapping Utilities
 */

import { Team, Ownership, ExtendedArchiMateDiagram } from './extended-types';
import { ArchiMateElement } from './types';
import { uuidv7 } from 'uuidv7';

export function createTeam(
  name: string,
  description: string,
  members: string[],
  lead: string,
  color?: string
): Team {
  return {
    id: uuidv7(),
    name,
    description,
    members,
    lead,
    color: color || generateTeamColor(name),
  };
}

function generateTeamColor(name: string): string {
  // Generate consistent color based on team name
  const colors = [
    '#3b82f6', // blue
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#f43f5e', // rose
    '#f97316', // orange
    '#eab308', // yellow
    '#84cc16', // lime
    '#10b981', // green
    '#14b8a6', // teal
    '#06b6d4', // cyan
  ];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
}

export function createOwnership(team: string, owner: string): Ownership {
  return {
    team,
    owner,
    raci: {
      responsible: [owner],
      accountable: owner,
      consulted: [],
      informed: [],
    },
  };
}

export function getElementsByTeam(
  diagram: ExtendedArchiMateDiagram,
  teamId: string
): ArchiMateElement[] {
  const team = diagram.teams.find((t) => t.id === teamId);
  if (!team) return [];

  return diagram.elements.filter(
    (element) => diagram.elementMetadata[element.id]?.ownership?.team === team.name
  );
}

export function getElementsByOwner(
  diagram: ExtendedArchiMateDiagram,
  owner: string
): ArchiMateElement[] {
  return diagram.elements.filter(
    (element) => diagram.elementMetadata[element.id]?.ownership?.owner === owner
  );
}

export function getUnownedElements(diagram: ExtendedArchiMateDiagram): ArchiMateElement[] {
  return diagram.elements.filter((element) => !diagram.elementMetadata[element.id]?.ownership);
}

export function getTeamWorkload(
  diagram: ExtendedArchiMateDiagram,
  teamId: string
): {
  team: Team;
  elementCount: number;
  complexityScore: number;
  elements: ArchiMateElement[];
} {
  const team = diagram.teams.find((t) => t.id === teamId);
  if (!team) {
    throw new Error(`Team not found: ${teamId}`);
  }

  const elements = getElementsByTeam(diagram, teamId);

  // Calculate complexity based on relationships and element types
  let complexityScore = 0;
  elements.forEach((element) => {
    // Base complexity by element type
    const baseComplexity = {
      strategy: 1,
      motivation: 1,
      business: 1,
      application: 2,
      technology: 3,
      physical: 2,
      implementation: 2,
    }[element.layer] || 1;

    // Add complexity for relationships
    const relCount = diagram.relationships.filter(
      (rel) => rel.sourceId === element.id || rel.targetId === element.id
    ).length;

    complexityScore += baseComplexity + relCount * 0.5;
  });

  return { team, elementCount: elements.length, complexityScore, elements };
}

export function getAllTeamsWorkload(diagram: ExtendedArchiMateDiagram): Array<{
  team: Team;
  elementCount: number;
  complexityScore: number;
}> {
  return diagram.teams.map((team) => {
    const { elementCount, complexityScore } = getTeamWorkload(diagram, team.id);
    return { team, elementCount, complexityScore };
  });
}

export function getTeamColor(diagram: ExtendedArchiMateDiagram, teamName: string): string {
  const team = diagram.teams.find((t) => t.name === teamName);
  return team?.color || '#6b7280'; // gray default
}

export function generateTeamHeatmap(diagram: ExtendedArchiMateDiagram): Record<string, string> {
  const heatmap: Record<string, string> = {};

  diagram.elements.forEach((element) => {
    const ownership = diagram.elementMetadata[element.id]?.ownership;
    if (ownership) {
      heatmap[element.id] = getTeamColor(diagram, ownership.team);
    } else {
      heatmap[element.id] = '#e5e7eb'; // gray for unowned
    }
  });

  return heatmap;
}

export function detectConwaysLawViolations(
  diagram: ExtendedArchiMateDiagram
): Array<{
  source: ArchiMateElement;
  target: ArchiMateElement;
  sourceTeam: string;
  targetTeam: string;
  reason: string;
}> {
  const violations: Array<{
    source: ArchiMateElement;
    target: ArchiMateElement;
    sourceTeam: string;
    targetTeam: string;
    reason: string;
  }> = [];

  // Check for high coupling between different teams
  const teamConnections = new Map<string, Map<string, number>>();

  diagram.relationships.forEach((rel) => {
    const source = diagram.elements.find((e) => e.id === rel.sourceId);
    const target = diagram.elements.find((e) => e.id === rel.targetId);

    if (!source || !target) return;

    const sourceTeam = diagram.elementMetadata[source.id]?.ownership?.team;
    const targetTeam = diagram.elementMetadata[target.id]?.ownership?.team;

    if (sourceTeam && targetTeam && sourceTeam !== targetTeam) {
      if (!teamConnections.has(sourceTeam)) {
        teamConnections.set(sourceTeam, new Map());
      }
      const connections = teamConnections.get(sourceTeam)!;
      connections.set(targetTeam, (connections.get(targetTeam) || 0) + 1);

      // Flag if more than 5 connections between different teams
      if (connections.get(targetTeam)! > 5) {
        violations.push({
          source,
          target,
          sourceTeam,
          targetTeam,
          reason: `High coupling between ${sourceTeam} and ${targetTeam} teams (${connections.get(targetTeam)} connections)`,
        });
      }
    }
  });

  return violations;
}

export function generateTeamOwnershipReport(diagram: ExtendedArchiMateDiagram): string {
  let report = `# Team Ownership Report\n\n`;
  report += `## ${diagram.name}\n\n`;
  report += `**Generated:** ${new Date().toISOString().split('T')[0]}\n\n`;

  // Team summary
  report += `## Teams (${diagram.teams.length})\n\n`;
  const workloads = getAllTeamsWorkload(diagram);
  workloads.forEach(({ team, elementCount, complexityScore }) => {
    report += `### ${team.name}\n`;
    report += `- **Lead:** ${team.lead}\n`;
    report += `- **Members:** ${team.members.length}\n`;
    report += `- **Elements Owned:** ${elementCount}\n`;
    report += `- **Complexity Score:** ${complexityScore.toFixed(1)}\n`;
    if (team.slackChannel) report += `- **Slack:** ${team.slackChannel}\n`;
    report += `\n`;
  });

  // Unowned elements
  const unowned = getUnownedElements(diagram);
  if (unowned.length > 0) {
    report += `## ⚠️ Unowned Elements (${unowned.length})\n\n`;
    unowned.forEach((element) => {
      report += `- ${element.name} (${element.type})\n`;
    });
    report += `\n`;
  }

  // Conway's Law violations
  const violations = detectConwaysLawViolations(diagram);
  if (violations.length > 0) {
    report += `## Conway's Law Violations\n\n`;
    report += `High coupling detected between teams:\n\n`;
    const uniqueViolations = new Map<string, number>();
    violations.forEach(({ sourceTeam, targetTeam }) => {
      const key = [sourceTeam, targetTeam].sort().join(' ↔ ');
      uniqueViolations.set(key, (uniqueViolations.get(key) || 0) + 1);
    });
    uniqueViolations.forEach((count, key) => {
      report += `- **${key}**: ${count} connections\n`;
    });
    report += `\n`;
  }

  return report;
}

export function suggestTeamRealignment(
  diagram: ExtendedArchiMateDiagram
): Array<{
  element: ArchiMateElement;
  currentTeam: string;
  suggestedTeam: string;
  reason: string;
}> {
  const suggestions: Array<{
    element: ArchiMateElement;
    currentTeam: string;
    suggestedTeam: string;
    reason: string;
  }> = [];

  // Analyze each element's connections
  diagram.elements.forEach((element) => {
    const currentOwnership = diagram.elementMetadata[element.id]?.ownership;
    if (!currentOwnership) return;

    // Count connections to each team
    const teamConnections = new Map<string, number>();

    diagram.relationships.forEach((rel) => {
      let connectedElementId: string | null = null;

      if (rel.sourceId === element.id) {
        connectedElementId = rel.targetId;
      } else if (rel.targetId === element.id) {
        connectedElementId = rel.sourceId;
      }

      if (connectedElementId) {
        const connectedElement = diagram.elements.find((e) => e.id === connectedElementId);
        if (connectedElement) {
          const connectedTeam =
            diagram.elementMetadata[connectedElement.id]?.ownership?.team;
          if (connectedTeam) {
            teamConnections.set(connectedTeam, (teamConnections.get(connectedTeam) || 0) + 1);
          }
        }
      }
    });

    // Find team with most connections
    let maxConnections = 0;
    let suggestedTeam = '';

    teamConnections.forEach((count, team) => {
      if (count > maxConnections && team !== currentOwnership.team) {
        maxConnections = count;
        suggestedTeam = team;
      }
    });

    // Suggest realignment if element has more connections to another team
    const currentTeamConnections = teamConnections.get(currentOwnership.team) || 0;
    if (maxConnections > currentTeamConnections * 1.5) {
      suggestions.push({
        element,
        currentTeam: currentOwnership.team,
        suggestedTeam,
        reason: `${maxConnections} connections to ${suggestedTeam} vs ${currentTeamConnections} to ${currentOwnership.team}`,
      });
    }
  });

  return suggestions;
}

export function exportTeamMappingToCSV(diagram: ExtendedArchiMateDiagram): string {
  let csv = 'Element,Type,Layer,Team,Owner,Slack Channel,On-Call\n';

  diagram.elements.forEach((element) => {
    const ownership = diagram.elementMetadata[element.id]?.ownership;
    csv += `"${element.name}",`;
    csv += `${element.type},`;
    csv += `${element.layer},`;
    csv += `"${ownership?.team || 'Unassigned'}",`;
    csv += `"${ownership?.owner || 'Unassigned'}",`;
    csv += `"${ownership?.slackChannel || ''}",`;
    csv += `"${ownership?.oncall || ''}"\n`;
  });

  return csv;
}

export function getRACIMatrix(diagram: ExtendedArchiMateDiagram): Record<
  string,
  {
    responsible: string[];
    accountable: string;
    consulted: string[];
    informed: string[];
  }
> {
  const matrix: Record<
    string,
    {
      responsible: string[];
      accountable: string;
      consulted: string[];
      informed: string[];
    }
  > = {};

  diagram.elements.forEach((element) => {
    const ownership = diagram.elementMetadata[element.id]?.ownership;
    if (ownership?.raci) {
      matrix[element.name] = ownership.raci;
    }
  });

  return matrix;
}

// Additional helper functions
export function assignOwnership(
  diagram: ExtendedArchiMateDiagram,
  elementId: string,
  ownership: Ownership
): ExtendedArchiMateDiagram {
  return {
    ...diagram,
    elementMetadata: {
      ...diagram.elementMetadata,
      [elementId]: {
        ...diagram.elementMetadata[elementId],
        ownership,
      },
    },
  };
}

export function detectOwnershipConflicts(diagram: ExtendedArchiMateDiagram): Array<{
  element: ArchiMateElement;
  conflict: string;
}> {
  const conflicts: Array<{ element: ArchiMateElement; conflict: string }> = [];

  diagram.elements.forEach((element) => {
    const ownership = diagram.elementMetadata[element.id]?.ownership;
    if (!ownership) return;

    // Check for RACI conflicts (multiple accountable)
    if (ownership.raci && ownership.raci.accountable && ownership.raci.accountable.includes(',')) {
      conflicts.push({
        element,
        conflict: 'Multiple accountable parties in RACI matrix',
      });
    }
  });

  return conflicts;
}

// Aliases for backward compatibility
export const analyzeConwaysLaw = detectConwaysLawViolations;
export const detectUnownedElements = getUnownedElements;
export const exportTeamsToCSV = exportTeamMappingToCSV;
export const generateRACIMatrix = getRACIMatrix;
